import React, { useEffect, useRef } from 'react'

import csrfHeaders from 'src/utils/csrfHeaders'

// Single submit path: every donation eventually flows through
// PaymentProcessors::StockDonationsController#create with an
// `order_id + processing_id`. In twoStep mode Launcher's Continue
// runs saveDraft up front and threads orderToken / orderUuid in. In
// single-step mode there's no Continue: onDonationSuccessful JIT-runs
// saveDraft itself via the `record` prop, then POSTs to the same
// controller. The SDK's externalId is `oid_{uuid}` when orderUuid
// is known at SDK-init time (twoStep) and falls back to the
// historical `${partner.id}_${external_id}` pair otherwise — the
// webhook's orphan-recovery path handles either form.
const Stock = ({
  ein, partner, partnerCode, partnerItem, projectId,
  submitUrl, scriptUrl, setDonation,
  orderToken, orderUuid, record, paymentMethod,
}) => {
  const containerRef = useRef()

  useEffect(() => {
    if (!containerRef.current || partnerItem.beneficiary.id == '_multiple') return

    while (containerRef.current.firstChild)
      containerRef.current.removeChild(containerRef.current.firstChild)

    const anotherContainer = document.createElement('div')
    containerRef.current.appendChild(anotherContainer)

    const onDonationSuccessful = async donationUuid => {
      // Hoisted so the catch block can attach the resolved token (the
      // single-step value, post-JIT-saveDraft) to Honeybadger context.
      // The `orderToken` prop is null in single-step, which is the path
      // most likely to fail.
      let token = orderToken
      try {
        if (!token) {
          // Single-step: no Continue prelude, so JIT-saveDraft now.
          // useRecord owns the order_id-via-references plumbing, so
          // a second call from the same donor would update the same
          // Order rather than creating a duplicate.
          const { errors, token: draftToken } = await record.saveDraft({
            principalOnly: true,
            order: { payment_source: paymentMethod },
          })
          if (errors) {
            throw Error('saveDraft failed', { cause: Error(JSON.stringify(errors, null, 2)) })
          }
          token = draftToken
        }

        const response = await fetch(submitUrl, {
          method: 'POST',
          headers: csrfHeaders(),
          body: JSON.stringify({ order_id: token, processing_id: donationUuid }),
        })

        if (!response.ok) {
          throw Error('Server side validation failed', {
            cause: Error(JSON.stringify(await response.json(), null, 2))
          })
        }

        // The new controller returns 204; the widget already has the
        // order details from saveDraft, so the partnerItem alone is
        // enough to drive the thank-you panel.
        setDonation({ partnerItem })
      } catch (error) {
        // Don't alarm the donor: the DonateStock UUID is enough for the
        // webhook to settle the order whether or not our flow landed —
        // via processing_id if the POST stamped it, otherwise via the
        // webhook's orphan-recovery path. Worst case is a feed delay
        // and (for a JIT-saveDraft failure) loss of project_id /
        // plugin_data, which would have been lost on the legacy
        // single-step flow too.
        setDonation({ partnerItem })
        console.error(error)
        Honeybadger.notify(error, {
          name: 'DonateStockError',
          context: { orderToken: token, donationUuid },
        })
      }
    }

    window['DonateStock-main'] = {
      q: [['init', {
        element: anotherContainer,
        // twoStep: orderUuid is set before the SDK mounts, so we use
        // `oid_{uuid}` for tight webhook→draft linkage. single-step:
        // saveDraft is JIT inside onDonationSuccessful, so orderUuid
        // isn't available at init and externalId falls back to the
        // historical pair. The webhook normally finds the order via
        // processing_id (stamped by StockDonationsController#create),
        // making externalId a fallback used only on
        // webhook-beats-post-success races and truly orphaned events.
        externalId: orderUuid
          ? `oid_${orderUuid}`
          : `${partner.id}_${partnerItem.external_id}`,
        fboEin: partnerItem.beneficiary.ein.replace(/-/g, ''),
        fboName: partnerItem.beneficiary.name,
        partnerCode,
        nativeWindow: true,
        preloadNonprofit: false,
        ein,
        debug: true,
        style: {
          dark: true,
        },
        onDonationSuccessful,
      }]],
    }

    const script = document.createElement('script')
    script.src = scriptUrl
    script.async = true
    script.id = 'DonateStock-main'
    document.body.appendChild(script)

    return () => {
      document.body.removeChild(script)
      window['loaded-DonateStock-main'] = false
      window['DonateStock-main'] = undefined
    }
  }, [ partnerItem.beneficiary.id, projectId, orderToken, orderUuid ])

  return <div ref={containerRef} className="text-center"></div>
}

export default Stock
